import { connect } from 'react-redux'
import { bindingTypes } from '@adalo/constants'
import { MultiMenuTrigger } from '@protonapp/react-multi-menu'
import {
  getComponent,
  getCurrentAppId,
  updateObject as updateObjectAction,
} from 'ducks/editor/objects'
import { getApp } from 'ducks/apps'
import {
  getDefaultDatasource,
  getDatasourcesObject,
} from 'ducks/apps/datasources'

import Icon from 'components/Shared/Icon'
import { GroupedAccordion } from 'components/Shared/Accordion'
import { Datasource } from 'components/Editor/XanoExternalDatabaseModal/lib/user'

import { getCollection } from 'utils/datasources'
import { buildBinding } from 'utils/bindings'
import { getFieldIcon } from 'utils/icons'

import {
  DatasourceCollection,
  Field,
  FormField,
  TableObject,
} from './TableTypes'
import { SortableFieldItemsList } from './TableField'
import { generateBindingSource } from './utils'
import ListControl from '../../ListControl'
import { THEMES } from '../../../../../constants'

interface TableProps {
  object: TableObject
  componentId: string
  datasourceCollection: DatasourceCollection
  datasource: Datasource
  updateObject: (id: string, changes: Partial<TableObject>) => void
  onChange: (changes: Partial<TableObject>) => void
}

type SortTableField = {
  oldIndex: number
  newIndex: number
}

const Table = ({
  object,
  componentId,
  datasourceCollection,
  datasource,
  updateObject,
  onChange,
}: TableProps): JSX.Element => {
  const update = (changes: Partial<TableObject>) => {
    updateObject(object.id, changes)
  }

  const handleSort = ({ oldIndex, newIndex }: SortTableField): void => {
    const { fields } = object

    const removedField = fields[oldIndex]

    if (!removedField) {
      console.error('failed to delete field', removedField)

      return
    }

    const newFields = fields.filter((_, i) => i !== oldIndex)

    const mergedFields = [
      ...newFields.slice(0, newIndex),
      removedField,
      ...newFields.slice(newIndex),
    ]

    update({ fields: mergedFields })
  }

  const handleDelete = (index: number) => {
    const { fields } = object

    const newFields = fields.filter((_, i) => i !== index)

    update({ fields: newFields })
  }

  const getFields = () => {
    const { collection, fields } = object

    if (!collection?.tableId) {
      console.error('collection not found')

      return
    }

    if (!datasource?.tables) {
      return
    }

    const tableFields = datasource.tables[collection.tableId]?.fields

    if (!tableFields) {
      console.error('table fields not found')

      return
    }

    const filteredFields = Object.keys(tableFields).filter(
      tableFieldId =>
        fields.findIndex(field => field.fieldId === tableFieldId) === -1
    )

    return filteredFields.map(tableFieldId => {
      const tableField = tableFields[tableFieldId]
      const icon = getFieldIcon(tableField?.type)

      return {
        key: tableFieldId,
        label: tableField?.name,
        value: tableFieldId,
        icon,
      }
    })
  }

  const handleAdd = (field: string) => {
    const { fields, collection } = object

    if (!collection?.tableId) {
      console.error('collection not found')

      return
    }

    const tableFields = datasource.tables[collection.tableId]?.fields

    if (!tableFields) {
      console.error('table fields not found')

      return
    }

    const tableField = tableFields[field]

    if (!tableField) {
      return
    }

    const source = generateBindingSource({
      fieldId: field,
      label: tableField.name,
      type: tableField.type as string,
      listObjectId: object.dataBinding?.id,
      source: object.dataBinding?.source,
    })

    const binding = buildBinding(bindingTypes.LIBRARY_PROP, source)

    const newFields = [...fields]

    newFields.push({
      fieldId: field,
      label: tableField.name,
      type: tableField.type as string,
      binding,
    })

    update({ fields: newFields })
  }

  const handleMenuChange = (field: Field, fieldIndex: number) => {
    const { fields } = object

    fields[fieldIndex] = {
      ...fields[fieldIndex],
      ...field,
    }

    update({ fields })
  }

  const formatTableFields = (fields: { [key: string]: FormField }): Field[] => {
    return Object.keys(fields).map(prop => {
      const field = fields[prop] as FormField

      const source = generateBindingSource({
        fieldId: prop,
        label: field.name,
        type: field.type,
        listObjectId: object.dataBinding?.id,
        source: object.dataBinding?.source,
      })

      return {
        fieldId: prop,
        label: field.name,
        type: field.type,
        binding: buildBinding(bindingTypes.LIBRARY_PROP, source),
      }
    })
  }

  const { fields } = object

  return (
    <g>
      <ListControl
        key="table-control"
        listTitle="Table Data"
        datasource={datasource}
        isTable
        hideActions
        hideComponents
        hideColumnSpacing
        hideAdvancedOptions
        componentId={componentId}
        object={object}
        onChange={onChange}
        formatTableFields={formatTableFields}
      />
      {fields.length > 0 && (
        <GroupedAccordion
          className="library-inspect-accordion"
          group="form-inspect"
          title="Columns"
          renderChildren={() => (
            <div className="form-inspect-fields-section">
              <p className="form-inspect-fields-title">Table Columns</p>
              <SortableFieldItemsList
                items={fields}
                collection={datasourceCollection}
                datasource={datasource}
                onDelete={handleDelete}
                onSortEnd={handleSort}
                onChange={handleMenuChange}
                distance={5}
              />
              <MultiMenuTrigger
                className="form-inspect-add-field"
                menu={getFields}
                onSelect={handleAdd}
                menuTheme={THEMES.DATA}
              >
                <Icon type="plus" />
                <span>Add Column</span>
              </MultiMenuTrigger>
            </div>
          )}
        />
      )}
    </g>
  )
}

const mapStateToProps = (
  state: Record<string, unknown>,
  { object }: { object: TableObject }
) => {
  const appId = getCurrentAppId(state) as string

  const app = getApp(state, appId)

  const component = getComponent(state, object.id)

  const dataBindings = object?.dataBinding?.source

  const tableId = dataBindings?.tableId ?? ''

  const datasource = getDefaultDatasource(state, appId) as Datasource

  const collection = {
    tableId,
    datasourceId: dataBindings?.datasourceId,
  }

  const datasourceCollection = getCollection(
    datasource,
    collection
  ) as DatasourceCollection

  return {
    app,
    appId,
    componentId: component?.id ?? '',
    datasources: getDatasourcesObject(state, appId) as Datasource[],
    datasource,
    object: {
      ...object,
      fields: object.fields || [],
      collection,
    },
    datasourceCollection,
  }
}

export default connect(mapStateToProps, { updateObject: updateObjectAction })(
  Table
)
